import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle } from "lucide-react";

import { knowledgeService } from "@/entities/knowledge/api/knowledgeService";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { QueryState } from "@/shared/ui/QueryState";
import { StatTile } from "@/shared/ui/StatTile";

const pct = (r: number) => `${Math.round(r * 100)}%`;

/**
 * KB 命中分析面板(G3.3)。近 N 天客服答问的**命中率** + **内容缺口**(客户问了、KB 没答上的问题)。
 *
 * 内容缺口是这个面板里**可行动**的那一半:补上这些问题对应的知识,命中率直接涨。窗口默认 7 天 ——
 * 单日计数每到 midnight 清零,对经理看"最近缺什么"没用;缺口也要按窗口累加才准(ai 侧做的聚合)。
 */
export function CoverageInsightPanel() {
  const [days, setDays] = useState<7 | 30>(7);
  const query = useQuery({
    queryKey: ["kb-coverage", days],
    queryFn: () => knowledgeService.getCoverageStats(days),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {([7, 30] as const).map((d) => (
          <Button
            key={d}
            size="sm"
            variant={days === d ? "default" : "outline"}
            onClick={() => setDays(d)}
          >
            近 {d} 天
          </Button>
        ))}
      </div>

      <QueryState
        query={query}
        isEmpty={(d) => d.hit + d.miss === 0}
        emptyTitle="这段时间还没有客服问答数据"
        emptyDescription="等客服 bot 接到客户提问后,这里会显示命中率和内容缺口。"
      >
        {(data) => (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <StatTile label="命中率" value={pct(data.hitRate)} />
              <StatTile label="总问答" value={data.hit + data.miss} />
              <StatTile label="未命中(内容缺口)" value={data.miss} />
            </div>

            {data.series.length > 0 && (
              <Card>
                <CardContent className="space-y-2 pt-4">
                  <div className="flex items-baseline justify-between">
                    <h3 className="text-sm font-semibold text-foreground">命中率趋势</h3>
                    <span className="text-xs text-muted-foreground">近 {data.days} 天逐日</span>
                  </div>
                  <div className="flex h-20 items-end gap-0.5">
                    {data.series.map((d) => {
                      const noTraffic = d.hit + d.miss === 0;
                      // 真空日(没问答)不能画成"命中率 0%",那会把"没流量"误读成"答得差"
                      const label = noTraffic
                        ? `${d.date} · 无问答`
                        : `${d.date} · 命中率 ${pct(d.hitRate)}`;
                      return (
                        <div
                          key={d.date}
                          title={label}
                          aria-label={label}
                          className="flex h-full flex-1 items-end"
                        >
                          <div
                            className={`w-full rounded-t ${noTraffic ? "bg-muted" : "bg-primary/80"}`}
                            style={{
                              height: noTraffic
                                ? "2px"
                                : `${Math.max(6, Math.round(d.hitRate * 100))}%`,
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between text-2xs text-muted-foreground">
                    <span>{data.startDate}</span>
                    <span>{data.endDate}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardContent className="space-y-3 pt-4">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">
                    内容缺口 · 客户问了、KB 没答上
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    补上这些问题对应的知识,命中率能直接提升。按近 {data.days} 天出现次数降序。
                  </p>
                </div>
                {data.topMissQuestions.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    近 {data.days} 天没有未命中的问题 —— KB 覆盖良好。
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {data.topMissQuestions.map((q) => (
                      <li
                        key={q.question}
                        className="flex items-center justify-between gap-3 text-sm"
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <AlertCircle className="h-4 w-4 shrink-0 text-amber-500" />
                          <span className="truncate">{q.question}</span>
                        </span>
                        <Badge variant="secondary" className="shrink-0">
                          {q.count} 次
                        </Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </QueryState>
    </div>
  );
}
